// Case 1: render the agent framework's built-in approval gate as buttons.
//
// Hermes/OpenClaw already emit a *text* approval prompt when the agent hits a
// dangerous command — e.g.
//
//     ⚠️ Dangerous command requires approval:
//     `rm -rf build`
//     Reply /approve to execute, /approve session to approve this pattern for the
//     session, /approve always to approve permanently, or /deny to cancel.
//
// The framework then waits for a `/approve…` / `/deny` text reply, which it parses
// itself. So we don't need the framework to change: detect that format and render
// the offered commands as buttons; a click just sends the matching text back.
//
// (Case 2 — the agent's *own* multi-select questions — is the native tool + Prompt
// card, built separately. This file is only the framework-approval bridge.)
import { useState } from 'react';

export type ApprovalCmd = { cmd: string; label: string; danger?: boolean };

// Display order: safe one-time Approve is primary (leftmost), Deny last. Each regex
// is self-contained (bare /approve has a negative lookahead), so detection does not
// depend on ordering.
const KNOWN: { re: RegExp; opt: ApprovalCmd }[] = [
  { re: /\/approve\b(?!\s+(?:session|always))/i, opt: { cmd: '/approve', label: 'Approve' } },
  { re: /\/approve\s+session\b/i, opt: { cmd: '/approve session', label: 'Approve for session' } },
  { re: /\/approve\s+always\b/i, opt: { cmd: '/approve always', label: 'Approve always' } },
  { re: /\/deny\b/i, opt: { cmd: '/deny', label: 'Deny', danger: true } },
];

// Returns the offered commands if `body` looks like the framework's approval gate,
// else null (so normal agent messages render untouched).
export function parseApproval(body?: string | null): ApprovalCmd[] | null {
  if (!body) return null;
  const low = body.toLowerCase();
  const looksApproval =
    low.includes('requires approval') ||
    (low.includes('/approve') && low.includes('/deny') && low.includes('reply'));
  if (!looksApproval) return null;
  const found = KNOWN.filter((k) => k.re.test(body)).map((k) => k.opt);
  return found.length ? found : null;
}

export function ApprovalActions({
  cmds, pending, onPick,
}: {
  cmds: ApprovalCmd[];
  // The gate blocks until answered, so an unanswered approval is always the newest
  // message. Once anything follows it, it's been handled — buttons go inert.
  pending: boolean;
  onPick: (cmd: string) => void | Promise<void>;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Collapse once answered — the user's reply + the agent's confirmation already
  // show the outcome, so leaving greyed buttons behind is just clutter. Gone the
  // moment you click (picked) or once anything follows the gate (!pending).
  if (picked || !pending) return null;
  return (
    <div className="fx-approval" role="group" aria-label="Approval">
      {cmds.map((c) => (
        <button
          key={c.cmd}
          type="button"
          className={`fx-approval-btn${c.danger ? ' is-deny' : ''}`}
          disabled={busy}
          onClick={async () => {
            setPicked(c.cmd);
            setBusy(true);
            try { await onPick(c.cmd); } finally { setBusy(false); }
          }}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}
